// Quotes for outside work (painting, welding, outside repairs): agreed and approved before the work;
// then paid against (Requests & payments) only to the quoted payee, with proof of the finished work.
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Page, Card, Btn, Table, Tr, Td, Field, Loading } from "@/components/kit";
import api from "@/lib/api";
import { act, Attach, DateIn, Files, input, k0, n, Num, opts, Select, State, useCan, useLoad, useRegisters } from "@/components/erp";
import { fmtDate, today } from "@/lib/money";

const QUOTE_CATS = [{ value: "painting", label: "Painting & sign writing" }, { value: "outside_labour", label: "Outside labour (fundis, welders)" },
  { value: "repairs", label: "Repairs (outside garage)" }];

export default function Quotes() {
  const can = useCan();
  const navigate = useNavigate();
  const [status, setStatus] = useState("");
  const [showNew, setShowNew] = useState(false);
  const { data, reload } = useLoad("/quotes", { status: status || undefined });
  return (
    <Page title="Quotes for outside work" actions={can("quote.request") && !showNew && <Btn kind="primary" onClick={() => setShowNew(true)}>+ New quote</Btn>}>
      <p className="mt-2 max-w-3xl text-sm text-slate-600">Outside work is agreed and approved <b>before</b> it starts. When it's done, pay it from Requests &amp; payments against the quote — only to the same person, with a photo of the finished work or a receipt.</p>
      {showNew && <NewQuote done={() => { setShowNew(false); reload(); }} />}
      <div className="mt-4 max-w-xs"><Field label="Status"><Select value={status} onChange={(v) => setStatus(v || "")} empty="All"
        options={[["pending", "Waiting approval"], ["approved", "Approved"], ["rejected", "Rejected"], ["closed", "Closed"]].map(([value, label]) => ({ value, label }))} /></Field></div>
      {!data ? <Loading /> : (
        <Table minWidth={980} cols={[{ label: "Date" }, { label: "Work" }, { label: "Truck" }, { label: "Who" }, { label: "Quoted", align: "right" }, { label: "Paid", align: "right" },
          { label: "Left", align: "right" }, { label: "Status" }, { label: "" }]}>
          {data.map((q) => (
            <Tr key={q.id}>
              <Td>{fmtDate(q.date)}</Td><Td>{q.category_label}<div className="text-xs text-slate-500">{q.description} <Files ids={q.attachments} /></div></Td>
              <Td>{q.truck}</Td><Td>{q.payee}</Td><Td right>{k0(q.amount)}</Td><Td right>{k0(q.paid)}</Td>
              <Td right className={q.left < 0 ? "text-rose-700" : ""}>{k0(q.left)}</Td><Td><State s={q.status} /></Td>
              <Td><div className="flex justify-end gap-2">
                {q.status === "approved" && can("expense.request") && <Btn kind="primary" onClick={() => navigate(`/spend?new=1&kind=expense`)}>Pay</Btn>}
                {["approved", "pending"].includes(q.status) && can("float.approve", "jobcard.approve") &&
                  <Btn onClick={async () => { if (window.confirm("Close this quote? Nothing more can be paid against it.") && await act(() => api.post(`/quotes/${q.id}/close`), "Closed")) reload(); }}>Close</Btn>}
              </div></Td>
            </Tr>
          ))}
        </Table>
      )}
    </Page>
  );
}

function NewQuote({ done }) {
  const regs = useRegisters(["trucks", "payees"]);
  const [q, setQ] = useState({ date: today(), category: "painting", attachments: [] });
  const set = (k) => (v) => setQ({ ...q, [k]: v });
  const payees = (regs.payees || []).filter((p) => p.status === "approved");
  return (
    <Card title="New quote" className="mt-4 max-w-3xl bg-white">
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <Field label="Kind of work"><Select value={q.category} empty={null} onChange={set("category")} options={QUOTE_CATS} /></Field>
        <Field label="Date"><DateIn value={q.date} onChange={set("date")} /></Field>
        <Field label="Who will do it (approved payee)"><Select value={q.payee_id} onChange={set("payee_id")} options={payees.map((p) => ({ value: p.id, label: `${p.name} (${p.trade})` }))} /></Field>
        <Field label="Truck (or leave empty for the yard)"><Select value={q.truck_id} onChange={set("truck_id")} options={opts(regs.trucks, "plate")} /></Field>
        <Field label="The work"><input className={input} value={q.description || ""} onChange={(e) => set("description")(e.target.value)} placeholder="e.g. Paint the cab and doors" /></Field>
        <Field label="Quoted amount (KES)"><Num value={q.amount} onChange={set("amount")} /></Field>
        <Field label="Job card no. (if any)"><input className={input} value={q.job_ref || ""} onChange={(e) => set("job_ref")(e.target.value)} /></Field>
      </div>
      {payees.length === 0 && <p className="mt-2 text-sm text-amber-700">No approved payees yet — Accounts adds them under Registers → Payees, and Finance approves them.</p>}
      <div className="mt-3"><Attach value={q.attachments} onChange={set("attachments")} label="Photo before / written quote" /></div>
      <div className="mt-4 flex gap-2">
        <Btn kind="primary" disabled={!q.payee_id || !n(q.amount) || !q.description} onClick={async () => { if (await act(() => api.post("/quotes", { ...q, amount: n(q.amount) }), "Quote sent for approval")) done(); }}>Submit for approval</Btn>
        <Btn onClick={done}>Cancel</Btn>
      </div>
    </Card>
  );
}
